/* @jsx h */
import { defineComponent, h, nextTick, ref, watch } from "vue";
import type { AdminRestaurantRecord } from "@lemmonhq/types";
import { ListMultiSelect } from "@lemmonhq/uikit";
import { RestaurantFormSection } from "./RestaurantFormSection";
import { RestaurantRegionalSettings } from "./RestaurantRegionalSettings";

export type ShopGroupOption = {
  id: number;
  name: string;
};

export type RestaurantFormValue = {
  name: string;
  slug: string;
  email: string;
  phone: string;
  contact_person: string;
  address: string;
  company_registration: string;
  timezone: string;
  locale: string;
  currency: string;
  jurisdiction: string;
  description: string;
  receipt_phone: string;
  receipt_message: string;
  manager_name: string;
  manager_email: string;
  manager_password: string;
  manager_id?: number | null;
  group_id?: number | null;
};

type FormSection = "details" | "regional" | "receipt" | "manager";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const createInitialValue = (
  restaurant?: AdminRestaurantRecord | null,
): RestaurantFormValue => ({
  name: restaurant?.name || "",
  slug: restaurant?.slug || "",
  email: restaurant?.email || "",
  phone: restaurant?.phone || "",
  contact_person: restaurant?.contact_person || "",
  address: restaurant?.address || "",
  company_registration: restaurant?.company_registration || "",
  timezone: restaurant?.timezone || "Europe/Zurich",
  locale: restaurant?.locale || "fr_CH",
  currency: restaurant?.currency || "CHF",
  jurisdiction: restaurant?.jurisdiction || "CH",
  description: restaurant?.description || "",
  receipt_phone: restaurant?.receipt_phone || "",
  receipt_message: restaurant?.receipt_message || "",
  manager_name: restaurant?.manager.name || "",
  manager_email: restaurant?.manager.email || "",
  manager_password: "",
  manager_id: restaurant?.manager.id || null,
  group_id: null,
});

export const RestaurantForm = defineComponent<{
  restaurant: AdminRestaurantRecord | null;
  submitting: boolean;
  shopGroups: ShopGroupOption[];
  onSubmit: (value: RestaurantFormValue) => Promise<void>;
  onCancel: () => void;
}>({
  name: "RestaurantForm",
  props: ["restaurant", "submitting", "shopGroups", "onSubmit", "onCancel"],
  setup(props) {
    const form = ref<RestaurantFormValue>(createInitialValue(props.restaurant));
    const activeSection = ref<FormSection>("details");
    const error = ref<string | null>(null);
    const slugTouched = ref(!!props.restaurant?.slug);

    watch(
      () => props.restaurant,
      (restaurant) => {
        form.value = createInitialValue(restaurant);
        slugTouched.value = !!restaurant?.slug;
        activeSection.value = "details";
        error.value = null;
      },
    );

    const submit = async (event: Event) => {
      event.preventDefault();
      error.value = null;

      const formElement = event.currentTarget as HTMLFormElement;
      if (!formElement.checkValidity()) {
        const invalidField = formElement.querySelector<HTMLElement>(":invalid");
        const section = invalidField?.closest<HTMLElement>("[data-section]")
          ?.dataset.section as FormSection | undefined;

        if (section) {
          activeSection.value = section;
          await nextTick();
        }
        invalidField?.focus();
        formElement.reportValidity();
        return;
      }

      try {
        await props.onSubmit({
          ...form.value,
          slug: form.value.slug.trim(),
        });
      } catch (nextError) {
        error.value =
          nextError instanceof Error
            ? nextError.message
            : "Failed to save restaurant";
      }
    };

    const toggleSection = (section: FormSection) => {
      activeSection.value = section;
    };

    const inputClass =
      "input input-bordered w-full bg-base-200 transition-colors focus:bg-base-100";
    const textareaClass =
      "textarea textarea-bordered min-h-[96px] w-full bg-base-200 transition-colors focus:bg-base-100";
    const labelClass = "label-text mb-2 font-medium text-base-content/80";

    return () => (
      <form class="flex h-full flex-col" novalidate onSubmit={submit}>
        <header class="border-b border-base-300 bg-base-100 px-5 py-5 sm:px-6">
          <div class="flex items-start gap-4">
            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span class="material-symbols-outlined">restaurant</span>
            </div>
            <div class="min-w-0">
              <p class="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Restaurant workspace
              </p>
              <h3 class="mt-1 truncate text-xl font-semibold text-base-content">
                {props.restaurant ? props.restaurant.name : "Create restaurant"}
              </h3>
              <p class="mt-1 text-sm text-base-content/60">
                Configure the profile, region, receipts, and primary manager.
              </p>
            </div>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto bg-base-200/40 px-4 py-5 sm:px-6">
          {error.value ? (
            <div class="alert alert-error mb-4">
              <span class="material-symbols-outlined">error</span>
              <span>{error.value}</span>
            </div>
          ) : null}

          <div class="space-y-3">
            <RestaurantFormSection
              id="details"
              title="Restaurant details"
              description="Business identity and contact information"
              icon="storefront"
              open={activeSection.value === "details"}
              onToggle={() => toggleSection("details")}
              render={() => (
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="form-control md:col-span-2">
                    <span class={labelClass}>Name *</span>
                    <input
                      class={inputClass}
                      value={form.value.name}
                      required
                      onInput={(event) => {
                        const value = (event.target as HTMLInputElement).value;
                        form.value.name = value;
                        if (!slugTouched.value)
                          form.value.slug = slugify(value);
                      }}
                    />
                  </label>

                  <label class="form-control">
                    <span class={labelClass}>Slug *</span>
                    <input
                      class={inputClass}
                      value={form.value.slug}
                      required
                      onInput={(event) => {
                        slugTouched.value = true;
                        form.value.slug = slugify(
                          (event.target as HTMLInputElement).value,
                        );
                      }}
                    />
                  </label>

                  <label class="form-control">
                    <span class={labelClass}>Restaurant email *</span>
                    <input
                      class={inputClass}
                      type="email"
                      value={form.value.email}
                      required
                      onInput={(event) =>
                        (form.value.email = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>

                  <label class="form-control">
                    <span class={labelClass}>Contact person *</span>
                    <input
                      class={inputClass}
                      value={form.value.contact_person}
                      required
                      onInput={(event) =>
                        (form.value.contact_person = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>

                  <label class="form-control">
                    <span class={labelClass}>Phone</span>
                    <input
                      class={inputClass}
                      type="tel"
                      value={form.value.phone}
                      onInput={(event) =>
                        (form.value.phone = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>

                  <label class="form-control md:col-span-2">
                    <span class={labelClass}>Address *</span>
                    <input
                      class={inputClass}
                      value={form.value.address}
                      required
                      onInput={(event) =>
                        (form.value.address = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>

                  <label class="form-control md:col-span-2">
                    <span class={labelClass}>Company registration</span>
                    <input
                      class={inputClass}
                      value={form.value.company_registration}
                      onInput={(event) =>
                        (form.value.company_registration = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>

                  <label class="form-control md:col-span-2">
                    <span class={labelClass}>Description</span>
                    <textarea
                      class={textareaClass}
                      value={form.value.description}
                      onInput={(event) =>
                        (form.value.description = (
                          event.target as HTMLTextAreaElement
                        ).value)
                      }
                    />
                  </label>

                  {!props.restaurant ? (
                    <div class="form-control md:col-span-2">
                      <span class={labelClass}>Shop group</span>
                      <ListMultiSelect
                        items={props.shopGroups}
                        selectedIds={
                          form.value.group_id ? [form.value.group_id] : []
                        }
                        buttonLabel="Shop group"
                        searchPlaceholder="Search shop groups"
                        widthClass="w-full"
                        onChange={(ids) => {
                          // Single selection: keep only the most recently
                          // toggled group. Clearing leaves it empty so the
                          // restaurant becomes its own singleton group.
                          form.value.group_id = ids.length
                            ? ids[ids.length - 1]
                            : null;
                        }}
                      />
                      <span class="mt-1 text-xs text-base-content/60">
                        Leave empty to create a new group for this restaurant.
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            />

            <RestaurantFormSection
              id="regional"
              title="Regional settings"
              description="Country, timezone, currency, and language"
              icon="language"
              open={activeSection.value === "regional"}
              onToggle={() => toggleSection("regional")}
              render={() => (
                <RestaurantRegionalSettings
                  jurisdiction={form.value.jurisdiction}
                  timezone={form.value.timezone}
                  currency={form.value.currency}
                  locale={form.value.locale}
                  onUpdate={(field, value) => {
                    form.value[field] = value;
                  }}
                />
              )}
            />

            <RestaurantFormSection
              id="receipt"
              title="Receipt settings"
              description="Customer-facing contact details and message"
              icon="receipt_long"
              open={activeSection.value === "receipt"}
              onToggle={() => toggleSection("receipt")}
              render={() => (
                <div class="grid gap-4">
                  <label class="form-control">
                    <span class={labelClass}>Receipt phone</span>
                    <input
                      class={inputClass}
                      type="tel"
                      value={form.value.receipt_phone}
                      onInput={(event) =>
                        (form.value.receipt_phone = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>
                  <label class="form-control">
                    <span class={labelClass}>Receipt message</span>
                    <textarea
                      class={textareaClass}
                      value={form.value.receipt_message}
                      onInput={(event) =>
                        (form.value.receipt_message = (
                          event.target as HTMLTextAreaElement
                        ).value)
                      }
                    />
                  </label>
                </div>
              )}
            />

            <RestaurantFormSection
              id="manager"
              title="Primary manager"
              description="Account owner and login credentials"
              icon="manage_accounts"
              open={activeSection.value === "manager"}
              onToggle={() => toggleSection("manager")}
              render={() => (
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="form-control">
                    <span class={labelClass}>Manager name *</span>
                    <input
                      class={inputClass}
                      value={form.value.manager_name}
                      required
                      onInput={(event) =>
                        (form.value.manager_name = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>
                  <label class="form-control">
                    <span class={labelClass}>Manager email *</span>
                    <input
                      class={inputClass}
                      type="email"
                      value={form.value.manager_email}
                      required
                      onInput={(event) =>
                        (form.value.manager_email = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>
                  <label class="form-control md:col-span-2">
                    <span class={labelClass}>
                      Password
                      {props.restaurant
                        ? " (leave blank to keep current)"
                        : " *"}
                    </span>
                    <input
                      class={inputClass}
                      type="password"
                      autocomplete="new-password"
                      value={form.value.manager_password}
                      required={!props.restaurant}
                      onInput={(event) =>
                        (form.value.manager_password = (
                          event.target as HTMLInputElement
                        ).value)
                      }
                    />
                  </label>
                </div>
              )}
            />
          </div>
        </div>

        <footer class="flex items-center justify-end gap-3 border-t border-base-300 bg-base-100 px-5 py-4 sm:px-6">
          <button
            type="button"
            class="btn btn-ghost"
            disabled={props.submitting}
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary min-w-32"
            disabled={props.submitting}
          >
            {props.submitting ? (
              <>
                <span class="loading loading-spinner loading-sm" /> Saving...
              </>
            ) : props.restaurant ? (
              "Save changes"
            ) : (
              "Create restaurant"
            )}
          </button>
        </footer>
      </form>
    );
  },
});
